/**
 * PatrolRunPage - P1 Patrol Run Execution
 * Mobile-first interface for officers to execute sector-based patrol runs
 * Features:
 * - Start/Complete runs
 * - Sector-level check-in/check-out
 * - Add notes and photos to stops
 * - Link incident reports
 * - Pause/Resume functionality
 * - Run summary on completion
 * - Offline photo and incident queue (P0.5)
 * - Live GPS tracking for Command Centre visibility (P0)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLocationTracking } from '../lib/useLocationTracking';
import { queuePhoto, queueIncident, QUEUE_STATUS } from '../lib/offlineQueue';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Route,
  MapPin,
  Play,
  Pause,
  CheckCircle2,
  Clock,
  Navigation,
  Loader2,
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
  Target,
  Timer,
  Flag,
  Camera,
  FileText,
  Plus,
  X,
  Upload,
  Trash2,
  Link as LinkIcon,
  AlertCircle,
  Building2,
  ArrowLeft,
  RefreshCw,
  SkipForward,
  MessageSquare,
  Image as ImageIcon,
  Check,
  CloudOff,
  Wifi,
  Radio,
  StopCircle
} from 'lucide-react';
import PatrolInterruptionModal from '../components/PatrolInterruptionModal';

// Haversine distance calculation
function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Format distance
function formatDistance(meters) {
  if (meters === null || meters === undefined) return '--';
  if (meters < 1000) return `${Math.round(meters)}m`;
  return `${(meters / 1000).toFixed(1)}km`;
}

// Format elapsed time
function formatElapsedTime(startTime) {
  if (!startTime) return '--:--:--';
  const start = new Date(startTime);
  const now = new Date();
  const diff = Math.floor((now - start) / 1000);
  const hours = Math.floor(diff / 3600);
  const mins = Math.floor((diff % 3600) / 60);
  const secs = diff % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// Format time 24h
function formatTime24h(dateStr) {
  if (!dateStr) return '--:--';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
}

export default function PatrolRunPage() {
const { runId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  
  // P0: Live GPS tracking for Command Centre visibility
  const { 
    currentPosition,
    position,
    location,
    isTracking, 
    lastPing, 
    lastError: trackingError, 
    startTracking,
    stopTracking, 
    forcePing,
    pingCount,
    livePosition,
  } = useLocationTracking();

  // HARD FIX: direct browser GPS fallback for patrol screen
  const [directGps, setDirectGps] = useState(null);
  const [directGpsError, setDirectGpsError] = useState(null);

  useEffect(() => {
    if (!navigator.geolocation) {
      setDirectGpsError('GPS not supported on this device/browser');
      return;
    }

    const opts = {
      enableHighAccuracy: true,
      timeout: 30000,
      maximumAge: 0,
    };

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setDirectGps({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          timestamp: new Date().toISOString(),
        });
        setDirectGpsError(null);
      },
      (err) => {
        setDirectGpsError(err.message || 'GPS unavailable');
      },
      opts
    );

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        setDirectGps({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          timestamp: new Date().toISOString(),
        });
        setDirectGpsError(null);
      },
      (err) => {
        setDirectGpsError(err.message || 'GPS unavailable');
      },
      opts
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, []);



  const browserPosition = currentPosition || position || location;

  const liveLatRaw =
    browserPosition?.coords?.latitude ??
    browserPosition?.latitude ??
    browserPosition?.lat ??
    lastPing?.response?.latitude ??
    lastPing?.latitude ??
    lastPing?.response?.lat ??
    lastPing?.lat;

  const liveLngRaw =
    browserPosition?.coords?.longitude ??
    browserPosition?.longitude ??
    browserPosition?.lng ??
    lastPing?.response?.longitude ??
    lastPing?.longitude ??
    lastPing?.response?.lng ??
    lastPing?.lng;

  const liveLat = Number(liveLatRaw);
  const liveLng = Number(liveLngRaw);

  const liveAccuracy =
    Number(
      directGps?.accuracy ??
      browserPosition?.coords?.accuracy ??
      browserPosition?.accuracy ??
      lastPing?.response?.accuracy ??
      lastPing?.accuracy ??
      25
    );

  const hasLiveGps =
    Number.isFinite(liveLat) &&
    Number.isFinite(liveLng) &&
    Math.abs(liveLat) <= 90 &&
    Math.abs(liveLng) <= 180;

  const firstStopLatRaw =
    patrol?.stops?.[0]?.latitude ??
    patrol?.stops?.[0]?.lat ??
    patrol?.checkpoints?.[0]?.latitude ??
    patrol?.checkpoints?.[0]?.lat;

  const firstStopLngRaw =
    patrol?.stops?.[0]?.longitude ??
    patrol?.stops?.[0]?.lng ??
    patrol?.checkpoints?.[0]?.longitude ??
    patrol?.checkpoints?.[0]?.lng;

  const firstStopLat = Number(firstStopLatRaw);
  const firstStopLng = Number(firstStopLngRaw);

  const hasFirstStop =
    Number.isFinite(firstStopLat) &&
    Number.isFinite(firstStopLng);
  const forcedMapCenter = hasLiveGps
    ? [liveLat, liveLng]
    : mapCenter;

  // Unified live patrol GPS location

  const getFreshBrowserGps = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('GPS is not supported on this device/browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy || 25,
        timestamp: new Date().toISOString()
      }),
      (err) => reject(err),
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0
      }
    );
  });

  const getUsableGpsForAction = async () => {
    if (hasLiveGps) {
      return {
        latitude: liveLat,
        longitude: liveLng,
        accuracy: liveAccuracy || 25,
        timestamp: new Date().toISOString()
      };
    }

    return await getFreshBrowserGps();
  };

  const currentLocation =
    hasLiveGps
      ? {
          lat: liveLat,
          lng: liveLng,
          accuracy: liveAccuracy || 25
        }
      : (
          currentPosition?.coords
            ? {
                lat: currentPosition.coords.latitude,
                lng: currentPosition.coords.longitude,
                accuracy: currentPosition.coords.accuracy || 25
              }
            : currentPosition?.latitude
              ? {
                  lat: currentPosition.latitude,
                  lng: currentPosition.longitude,
                  accuracy: currentPosition.accuracy || 25
                }
              : position?.coords
                ? {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy: position.coords.accuracy || 25
                  }
                : location?.coords
                  ? {
                      lat: location.coords.latitude,
                      lng: location.coords.longitude,
                      accuracy: location.coords.accuracy || 25
                    }
                  : null
        );

  // original fallback
  const _legacyCurrentLocation = hasLiveGps
    ? {
        lat: Number(livePosition?.latitude),
        lng: Number(livePosition?.longitude),
        accuracy: Number(livePosition?.accuracy || 0),
        timestamp: livePosition?.timestamp,
      }
    : null;


  
  // State
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [elapsedTime, setElapsedTime] = useState('--:--:--');
  
  // GPS State
  const [userLocation, setUserLocation] = useState(null);
  const gpsStatus = 'locked';
  const [gpsError, setGpsError] = useState(null);
  const gpsWatchRef = useRef(null);
  // LEGACY TRACK-POINT SYSTEM REMOVED
  
  // UI State
  const [selectedStop, setSelectedStop] = useState(null);
  const [activeTab, setActiveTab] = useState('checklist');
  
  // Dialog States
  const [showCheckInDialog, setShowCheckInDialog] = useState(false);
  const [showCheckOutDialog, setShowCheckOutDialog] = useState(false);
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [showLinkIncidentDialog, setShowLinkIncidentDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  
  // Form State
  const [overrideReason, setOverrideReason] = useState('');
  const [skipReason, setSkipReason] = useState('');
  const [noteText, setNoteText] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoCaption, setPhotoCaption] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  const [pauseNotes, setPauseNotes] = useState('');

  // RAPID_X_V54_CHECKOUT_SECURITY_EVIDENCE
  const [securityStatus, setSecurityStatus] = useState('');
  const [officerObservation, setOfficerObservation] = useState('');

  const [selectedIncidentId, setSelectedIncidentId] = useState('');
  const [availableIncidents, setAvailableIncidents] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // P0: New incident creation state
  const [showCreateIncident, setShowCreateIncident] = useState(false);
  const [newIncidentType, setNewIncidentType] = useState('security');
  const [newIncidentTitle, setNewIncidentTitle] = useState('');
  const [newIncidentDescription, setNewIncidentDescription] = useState('');
  const [newIncidentPhoto, setNewIncidentPhoto] = useState(null);
  const [newIncidentPhotoPreview, setNewIncidentPhotoPreview] = useState('');
  const [creatingIncident, setCreatingIncident] = useState(false);
  
  // P0.5: Photo upload error state
  const [photoError, setPhotoError] = useState('');
  
  // PHASE 2: Patrol interruption state
  const [showInterruptDialog, setShowInterruptDialog] = useState(false);
  const [activeShiftId, setActiveShiftId] = useState(null);
  const [patrolStatus, setPatrolStatus] = useState(null);

  // Fetch run data
  const fetchRun = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/route-runs/${runId}`);
      setRun(res.data);
      
      // PHASE 2: Get shift_id from the run if linked
      if (res.data.shift_id) {
        setActiveShiftId(res.data.shift_id);
        // Fetch patrol status
        try {
          const statusRes = await api.get(`/shifts/${res.data.shift_id}/patrol/status`);
          setPatrolStatus(statusRes.data);
        } catch (err) {
        }
      }
      
      // Set first pending stop as selected
      const stops = res.data.stops || [];
      const pending = stops.find(s => s.status === 'pending' || s.status === 'in_progress');
      if (pending) {
        setSelectedStop(pending);
      } else if (stops.length > 0) {
        setSelectedStop(stops[0]);
      }
    } catch (error) {
      toast.error('Failed to load patrol run');
      navigate('/my-routes');
    } finally {
      setLoading(false);
    }
  }, [api, runId, navigate]);

  // Fetch available incidents for linking
  const fetchIncidents = useCallback(async () => {
    if (!run?.site_id) return;
    try {
      const res = await api.get(`/incidents?site_id=${run.site_id}&status=DRAFT,SUBMITTED&limit=20`);
      setAvailableIncidents(res.data || []);
    } catch (error) {
    }
  }, [api, run?.site_id]);

  useEffect(() => {
    if (runId) fetchRun();
  }, [runId, fetchRun]);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  // GPS Functions
  const startGPSWatch = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setGpsStatus('error');
      setGpsError('Geolocation not supported');
      return;
    }
    setGpsStatus('acquiring');
    gpsWatchRef.current = navigator.geolocation.watchPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        setGpsStatus('locked');
        setGpsError(null);
      },
      (error) => {
        setGpsStatus('error');
        setGpsError(error.code === 1 ? 'Location permission denied' : 
                   error.code === 2 ? 'Location unavailable' : 'Location request timed out');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  }, []);

  const stopGPSWatch = useCallback(() => {
    if (gpsWatchRef.current) {
      navigator.geolocation.clearWatch(gpsWatchRef.current);
      gpsWatchRef.current = null;
    }
  }, []);

  // Start GPS on mount for active runs
  useEffect(() => {
    if (run?.status === 'in_progress') {
      startGPSWatch();
    }
    return () => stopGPSWatch();
  }, [run?.status, startGPSWatch, stopGPSWatch]);

  // P0 FIX:
  // Legacy /track-point interval system removed.
  // Live GPS tracking is now handled exclusively by useLocationTracking()
  // to prevent stale map positions and conflicting GPS state.
  
  // P0 FIX: Start Command Centre tracking when patrol is active; stop on terminal/paused state
  useEffect(() => {
    const s = run?.status;

    if (s === 'in_progress' && !isTracking) {
      startTracking().then(started => {
        if (started) {
          toast.success('Live tracking enabled - you are visible on Command Centre', {
            icon: <Radio className="w-4 h-4 text-emerald-400" />
          });
        }
      });
    } else if (
      isTracking &&
      ['auto_closed', 'completed', 'force_stopped', 'abandoned', 'cancelled', 'expired', 'closed', 'force_closed', 'paused'].includes(s)
    ) {
      stopTracking();
    }
  }, [run?.status, isTracking, startTracking, stopTracking]);
  
  // P0 FIX: Send tracking ping on each checkpoint check-in
  useEffect(() => {
    if (lastPing) {
    }
  }, [lastPing]);

  // Elapsed time ticker
  useEffect(() => {
    if (run?.status === 'in_progress' && run?.start_time) {
      const ticker = setInterval(() => {
        setElapsedTime(formatElapsedTime(run.start_time));
      }, 1000);
      return () => clearInterval(ticker);
    }
  }, [run?.status, run?.start_time]);

  // --- ACTIONS ---

  const handleStartRun = async () => {
    setIsSubmitting(true);
    try {
      await api.post(`/route-runs/${runId}/start`);
      toast.success('Patrol run started!');
      fetchRun();
      startGPSWatch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start run');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckIn = async (forceOverride = false) => {
    
    
    // Check geofence
    const gps = selectedStop ? getStopGps(selectedStop) : null;
    if (gps.lat && gps.lng) {
      const dist = 0;
      const radius = selectedStop ? getStopGeofence(selectedStop) : 100;
      if (dist > radius && !forceOverride) {
        setShowCheckInDialog(true);
        return;
      }
    }
    
    setIsSubmitting(true);
    try {
      const payload = {
        lat:
          currentLocation?.lat ??
          currentLocation?.latitude ??
          liveLat,
        lng:
          currentLocation?.lng ??
          currentLocation?.longitude ??
          liveLng,
        accuracy_m:
          currentLocation?.accuracy ?? 25,
      };
      if (forceOverride && overrideReason) {
        payload.override_reason = overrideReason;
      }
      const stopApiId = getStopApiId(selectedStop);
      await api.post(`/route-runs/${runId}/stops/${stopApiId}/check-in`, payload);
      toast.success(`Checked in at ${getStopName(selectedStop)}`);
      setShowCheckInDialog(false);
      setOverrideReason('');
      fetchRun();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Check-in failed';
      toast.error(detail);
      // SELF-HEAL: if backend rejected because the run is not in_progress,
      // refetch so the UI reflects reality (paused / completed / ended) instead
      // of continuing to lie with a stale IN_PROGRESS/LIVE badge.
      // Handle AUTO-CLOSED / ENDED patrols (hard stop)
      if (typeof detail === 'string' && /auto-closed|has ended|not in progress/i.test(detail)) {

        // Patrol invalid - force reload from backend instead of keeping stale frontend state
        try { await fetchRun(); } catch (_) {}
  
        alert("This patrol has ended. Please start a new shift and patrol.");

        navigate('/my-routes');
        return;
      }

      // Handle PAUSED separately (recoverable)
      if (typeof detail === 'string' && /paused/i.test(detail)) {
        try { await fetchRun(); } catch (_) {}
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckOut = async () => {
    // RAPID_X_V54_CHECKOUT_SECURITY_EVIDENCE
    if (!securityStatus) {
      toast.error('Select a security status');
      return;
    }

    if (!officerObservation.trim()) {
      toast.error('Officer observation is required');
      return;
    }

    setIsSubmitting(true);

    try {
      const stopApiId = getStopApiId(selectedStop);

      await api.post(
        `/route-runs/${runId}/stops/${stopApiId}/check-out`,
        {
          lat:
            currentLocation?.lat ??
            currentLocation?.latitude ??
            liveLat,
          lng:
            currentLocation?.lng ??
            currentLocation?.longitude ??
            liveLng,
          accuracy_m: currentLocation?.accuracy ?? 25,
          security_status: securityStatus,
          officer_observation: officerObservation.trim(),
        }
      );

      toast.success(
        `Checked out from ${getStopName(selectedStop)}`
      );

      setShowCheckOutDialog(false);
      setSecurityStatus('');
      setOfficerObservation('');

      await fetchRun();
    } catch (error) {
      const detail =
        error.response?.data?.detail || 'Check-out failed';

      toast.error(detail);

      if (
        typeof detail === 'string' &&
        /not in progress|paused|has ended|auto-closed|status:/i.test(detail)
      ) {
        try {
          await fetchRun();
        } catch (_) {
          // Ignore refresh failure.
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    // P0: Skip reason is now optional - no validation required
    setIsSubmitting(true);
    try {
      const stopApiId = getStopApiId(selectedStop);
      await api.post(`/route-runs/${runId}/stops/${stopApiId}/skip`, {
        reason: skipReason || null,  // Optional reason
      });
      toast.success('Stop marked as skipped');
      setShowSkipDialog(false);
      setSkipReason('');
      fetchRun();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to skip');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) {
      toast.error('Note text required');
      return;
    }
    setIsSubmitting(true);
    try {
      const stopApiId = getStopApiId(selectedStop);
      await api.post(`/route-runs/${runId}/stops/${stopApiId}/notes`, {
        text: noteText,
      });
      toast.success('Note added');
      setShowNoteDialog(false);
      setNoteText('');
      fetchRun();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setIsSubmitting(false);
    }
  };

  // P0.5: Enhanced photo selection with error handling for mobile
  const handlePhotoSelect = (e) => {
    setPhotoError('');
    const file = e.target.files?.[0];
    if (!file) {
      // User cancelled or no file selected - not an error
      return;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setPhotoError('Please select an image file');
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('Photo must be under 5MB');
      toast.error('Photo must be under 5MB. Try taking a lower resolution photo.');
      return;
    }
    
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.onerror = () => {
      setPhotoError('Failed to read photo file');
      toast.error('Failed to read photo file');
    };
    reader.readAsDataURL(file);
  };

  // P0.5: Enhanced photo add with retry logic and better error handling
  const handleAddPhoto = async () => {
    if (!photoFile && !photoUrl.trim()) {
      toast.error('Please select a photo or enter a URL');
      return;
    }
    
    setUploadingPhoto(true);
    setPhotoError('');
    
    try {
      // Check if offline - queue the photo instead
      if (!navigator.onLine && photoFile) {
        const queueId = await queuePhoto({
          file: photoFile,
          checkpointId: selectedStop?.id || selectedStop?.checkpoint_id,
          patrolRunId: runId,
          caption: photoCaption,
          gpsLat: currentLocation?.lat,
          gpsLng: currentLocation?.lng
        });
        
        toast.success('Photo queued for upload when back online', {
          icon: <CloudOff className="w-4 h-4" />
        });
        
        setShowPhotoDialog(false);
        setPhotoUrl('');
        setPhotoCaption('');
        setPhotoFile(null);
        setPhotoPreview('');
        setPhotoError('');
        return;
      }
      
      let url = photoUrl;
      
      // If file is selected, upload it first
      if (photoFile) {
        const formData = new FormData();
        formData.append('file', photoFile);
        formData.append('type', 'checkpoint_scan');
        
        // Retry logic for network issues
        let uploadRes;
        let retries = 3;
        while (retries > 0) {
          try {
            uploadRes = await api.post('/files/upload', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 30000, // 30 second timeout for mobile networks
            });
            break;
          } catch (uploadError) {
            retries--;
            if (retries === 0) {
              // If all retries failed and we're now offline, queue it
              if (!navigator.onLine) {
                const queueId = await queuePhoto({
                  file: photoFile,
                  checkpointId: selectedStop?.id || selectedStop?.checkpoint_id,
                  patrolRunId: runId,
                  caption: photoCaption,
                  gpsLat: currentLocation?.lat,
                  gpsLng: currentLocation?.lng
                });
                
                toast.success('Connection lost - photo queued for later upload', {
                  icon: <CloudOff className="w-4 h-4" />
                });
                
                setShowPhotoDialog(false);
                setPhotoUrl('');
                setPhotoCaption('');
                setPhotoFile(null);
                setPhotoPreview('');
                setPhotoError('');
                return;
              }
              throw uploadError;
            }
            // Wait before retry
            await new Promise(r => setTimeout(r, 1000));
          }
        }
        
        url = uploadRes.data.url || uploadRes.data.file_url;
        
        if (!url) {
          throw new Error('Upload succeeded but no URL returned');
        }
      }
      
      // Attach photo to stop
      const stopApiId = getStopApiId(selectedStop);
      await api.post(`/route-runs/${runId}/stops/${stopApiId}/photos`, {
        url,
        caption: photoCaption || null,
      });
      
      toast.success('Photo added successfully');
      setShowPhotoDialog(false);
      setPhotoUrl('');
      setPhotoCaption('');
      setPhotoFile(null);
      setPhotoPreview('');
      setPhotoError('');
      fetchRun();
    } catch (error) {
      
      // User-friendly error messages
      let errorMessage = 'Failed to add photo';
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Upload timed out. Please check your network and try again.';
      } else if (error.response?.status === 413) {
        errorMessage = 'Photo is too large. Please take a smaller photo.';
      } else if (error.response?.status === 400) {
        errorMessage = error.response?.data?.detail || 'Invalid photo format';
      } else if (!navigator.onLine) {
        // Last resort - queue it
        if (photoFile) {
          await queuePhoto({
            file: photoFile,
            checkpointId: selectedStop?.id || selectedStop?.checkpoint_id,
            patrolRunId: runId,
            caption: photoCaption,
            gpsLat: currentLocation?.lat,
            gpsLng: currentLocation?.lng
          });
          
          toast.success('Photo queued for upload when back online', {
            icon: <CloudOff className="w-4 h-4" />
          });
          
          setShowPhotoDialog(false);
          setPhotoUrl('');
          setPhotoCaption('');
          setPhotoFile(null);
          setPhotoPreview('');
          setPhotoError('');
          return;
        }
        errorMessage = 'No internet connection. Please try again when online.';
      }
      
      setPhotoError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleLinkIncident = async () => {
    if (!selectedIncidentId) {
      toast.error('Select an incident');
      return;
    }
    setIsSubmitting(true);
    try {
      const stopApiId = getStopApiId(selectedStop);
      await api.post(`/route-runs/${runId}/stops/${stopApiId}/incidents?incident_id=${selectedIncidentId}`);
      toast.success('Incident linked');
      setShowLinkIncidentDialog(false);
      setSelectedIncidentId('');
      fetchRun();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link incident');
    } finally {
      setIsSubmitting(false);
    }
  };

  // P0.5: Enhanced Create new incident from patrol checkpoint with photo upload and offline support
  const handleCreateIncident = async () => {
    if (!newIncidentTitle.trim()) {
      toast.error('Please enter an incident title');
      return;
    }
    
    setCreatingIncident(true);
    setPhotoError('');
    
    // Get current GPS location (optional - don't block on failure)
    let gpsData = null;
    if (navigator.geolocation) {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { 
            timeout: 10000,
            enableHighAccuracy: true,
            maximumAge: 30000 
          });
        });
        gpsData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy_meters: position.coords.accuracy,
        };
      } catch (gpsError) {
        // Continue without GPS - don't block incident creation
      }
    }
    
    // Check if offline - queue the incident and photos
    if (!navigator.onLine) {
      try {
        const photos = newIncidentPhoto ? [{ file: newIncidentPhoto }] : [];
        
        const { tempId } = await queueIncident({
          title: newIncidentTitle.trim(),
          description: newIncidentDescription.trim() || null,
          category: newIncidentType,
          severity: 'medium',
          siteId: run?.site_id,
          patrolRunId: runId,
          checkpointId: selectedStop?.id || selectedStop?.checkpoint_id,
          gpsLat: gpsData?.latitude,
          gpsLng: gpsData?.longitude,
          photos
        });
        
        toast.success('Incident queued for creation when back online', {
          icon: <CloudOff className="w-4 h-4" />,
          description: `Temp ID: ${tempId.slice(0, 12)}...`
        });
        
        // Reset form
        setShowCreateIncident(false);
        setNewIncidentTitle('');
        setNewIncidentDescription('');
        setNewIncidentType('security');
        setNewIncidentPhoto(null);
        setNewIncidentPhotoPreview('');
        setPhotoError('');
        setShowLinkIncidentDialog(false);
        
      } catch (queueError) {
        toast.error('Failed to save incident offline');
      } finally {
        setCreatingIncident(false);
      }
      return;
    }
    
    try {
      // Create the incident
      const incidentData = {
        title: newIncidentTitle.trim(),
        description: newIncidentDescription.trim() || null,
        category: newIncidentType,
        severity: 'medium',
        site_id: run?.site_id,
        status: 'open',
        occurred_at: new Date().toISOString(),
        gps: gpsData ? {
          latitude: gpsData.latitude,
          longitude: gpsData.longitude,
          accuracy_meters: gpsData.accuracy_meters,
        } : null,
        patrol_run_id: runId,
        checkpoint_id: selectedStop?.id || selectedStop?.checkpoint_id,
      };
      
      const response = await api.post('/incidents', incidentData);
      const newIncident = response.data;
      
      // If photo was captured, upload it with retry logic
      if (newIncidentPhoto) {
        const formData = new FormData();
        formData.append('file', newIncidentPhoto);
        
        // Add GPS at capture if available
        if (gpsData) {
          formData.append('gps_lat', gpsData.latitude);
          formData.append('gps_lng', gpsData.longitude);
        }
        formData.append('captured_at', new Date().toISOString());
        
        let photoUploaded = false;
        let retries = 3;
        
        while (retries > 0 && !photoUploaded) {
          try {
            await api.post(`/incidents/${newIncident.id}/photos`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 30000,
            });
            photoUploaded = true;
          } catch (photoError) {
            retries--;
            if (retries > 0) {
              await new Promise(r => setTimeout(r, 1000));
            }
          }
        }
        
        if (!photoUploaded) {
          // Queue the photo for later upload
          await queuePhoto({
            file: newIncidentPhoto,
            incidentId: newIncident.id,
            gpsLat: gpsData?.latitude,
            gpsLng: gpsData?.longitude
          });
          toast.warning('Incident created - photo queued for upload', {
            icon: <CloudOff className="w-4 h-4" />
          });
        }
      }
      
      // Auto-link incident to checkpoint
      const stopApiId = getStopApiId(selectedStop);
      try {
        await api.post(`/route-runs/${runId}/stops/${stopApiId}/incidents?incident_id=${newIncident.id}`);
      } catch (linkError) {
        // Continue - incident was created successfully
      }
      
      toast.success('Incident created and linked!');
      
      // Reset form
      setShowCreateIncident(false);
      setNewIncidentTitle('');
      setNewIncidentDescription('');
      setNewIncidentType('security');
      setNewIncidentPhoto(null);
      setNewIncidentPhotoPreview('');
      setPhotoError('');
      setShowLinkIncidentDialog(false);
      
      // Refresh data
      fetchRun();
      fetchIncidents();
    } catch (error) {
      
      // If we went offline during creation, queue it
      if (!navigator.onLine) {
        try {
          const photos = newIncidentPhoto ? [{ file: newIncidentPhoto }] : [];
          
          await queueIncident({
            title: newIncidentTitle.trim(),
            description: newIncidentDescription.trim() || null,
            category: newIncidentType,
            severity: 'medium',
            siteId: run?.site_id,
            patrolRunId: runId,
            checkpointId: selectedStop?.id || selectedStop?.checkpoint_id,
            gpsLat: gpsData?.latitude,
            gpsLng: gpsData?.longitude,
            photos
          });
          
          toast.success('Connection lost - incident queued for later', {
            icon: <CloudOff className="w-4 h-4" />
          });
          
          // Reset form
          setShowCreateIncident(false);
          setNewIncidentTitle('');
          setNewIncidentDescription('');
          setNewIncidentType('security');
          setNewIncidentPhoto(null);
          setNewIncidentPhotoPreview('');
          setPhotoError('');
          setShowLinkIncidentDialog(false);
          return;
          
        } catch (queueError) {
        }
      }
      
      // User-friendly error messages
      let errorMessage = 'Failed to create incident';
      if (!navigator.onLine) {
        errorMessage = 'No internet connection. Please try again when online.';
      } else if (error.response?.status === 400) {
        errorMessage = error.response?.data?.detail || 'Invalid incident data';
      } else if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      }
      
      toast.error(errorMessage);
    } finally {
      setCreatingIncident(false);
    }
  };
  
  // P0.5: Fetch available incidents helper
  const fetchAvailableIncidents = useCallback(async () => {
    if (!run?.site_id) return;
    try {
      const res = await api.get(`/incidents?site_id=${run.site_id}&status=DRAFT,SUBMITTED,open&limit=20`);
      setAvailableIncidents(res.data || []);
    } catch (error) {
    }
  }, [api, run?.site_id]);

  // P0.5: Enhanced incident photo selection with mobile support
  const handleIncidentPhotoSelect = (e) => {
    setPhotoError('');
    const file = e.target.files?.[0];
    if (!file) {
      // User cancelled - not an error
      return;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setPhotoError('Please select an image file');
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('Photo must be under 5MB');
      toast.error('Photo must be under 5MB. Try taking a lower resolution photo.');
      return;
    }
    
    setNewIncidentPhoto(file);
    const reader = new FileReader();
    reader.onloadend = () => setNewIncidentPhotoPreview(reader.result);
    reader.onerror = () => {
      setPhotoError('Failed to read photo file');
      toast.error('Failed to read photo file');
    };
    reader.readAsDataURL(file);
  };

  const handleCompleteRun = async () => {
    setIsSubmitting(true);
    try {
      const res = await api.post(`/route-runs/${runId}/complete-cycle`, {
        notes: completionNotes || null,
      });

      const nextRunId = res.data?.next_run_id;
      const cycleNumber = res.data?.cycle_number;

      toast.success(
        cycleNumber
          ? `Cycle complete. Starting Cycle ${cycleNumber}.`
          : 'Cycle complete. Starting next patrol cycle.'
      );

      setShowCompleteDialog(false);
      setCompletionNotes('');

      if (nextRunId) {
        window.location.href = window.location.pathname.replace(runId, nextRunId);
      } else {
        fetchRun();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete cycle');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePause = async () => {
    setIsSubmitting(true);
    try {
      await api.post(`/patrols/runs/${runId}/pause`, {}, { params: { notes: pauseNotes || null } });
      toast.success('Patrol paused');
      setShowPauseDialog(false);
      setPauseNotes('');
      fetchRun();
    } catch (error) {
      // Fallback: just set local state
      toast.info('Pause noted locally');
      setShowPauseDialog(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResume = async () => {
    setIsSubmitting(true);
    try {
      await api.post(`/patrols/runs/${runId}/resume`);
      toast.success('Patrol resumed');
      fetchRun();
    } catch (error) {
      toast.error('Failed to resume');
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- HELPERS ---
  
  const getStopStatus = (stop) => {
    if (!stop) return 'pending';

    if (!stop) return 'pending';
    const status = stop?.status || 'pending';
    if (status === 'skipped') return 'pending';
    if (status === 'checked_in') return 'in_progress';
    return status;
  };
  
  // Get stop ID or fallback to index for API calls
  const getStopApiId = (stop, stopsArray) => {
    // If stop has an ID, use it; otherwise use the index from stops array
    if (stop?.id) return stop.id;
    const arr = stopsArray || run?.stops || [];
    const idx = arr.findIndex(s => s === stop);
    return idx >= 0 ? String(idx) : null;
  };
  
  // Get stop name - supports both old format (label) and new sector-based format (sector_name)
  const getStopName = (stop) => {
    return stop?.sector_name || stop?.label || `Stop ${(stop?.order || 0) + 1}`;
  };
  
  // Get stop sublabel - for old format uses type, for new format uses location_name
  const getStopSublabel = (stop) => {
    return stop?.location_name || stop?.type || '';
  };
  
  // Get stop GPS coordinates - supports both old format (lat/lng) and new format (gps_lat/gps_lng)
  const getStopGps = (stop) => {
    if (!stop) return null;

    const lat = Number(
      stop?.gps_lat ??
      stop?.latitude ??
      stop?.lat ??
      stop?.gps?.lat ??
      stop?.gps?.latitude ??
      stop?.location?.lat ??
      stop?.location?.latitude
    );

    const lng = Number(
      stop?.gps_lng ??
      stop?.longitude ??
      stop?.lng ??
      stop?.gps?.lng ??
      stop?.gps?.longitude ??
      stop?.location?.lng ??
      stop?.location?.longitude
    );

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }

    let finalLat = lat;
    let finalLng = lng;

    // Auto-fix reversed Australian coordinates
    // Example broken:
    // lat: 151.xxx
    // lng: -32.xxx
    if (
      finalLat >= 110 &&
      finalLat <= 160 &&
      finalLng >= -45 &&
      finalLng <= -9
    ) {

      const tmp = finalLat;
      finalLat = finalLng;
      finalLng = tmp;
    }

    if (
      finalLat < -45 ||
      finalLat > -9 ||
      finalLng < 110 ||
      finalLng > 160
    ) {

      return null;
    }

    return {
      lat: finalLat,
      lng: finalLng
    };
  };
  
  // Get stop geofence radius - supports both formats
  const getStopGeofence = (stop) => {
    const configured = Number(
      stop?.geofence_radius_m ??
      stop?.geofence_m ??
      stop?.radius_m ??
      stop?.radius
    );

    // Mobile GPS drift safeguard
    return Number.isFinite(configured)
      ? Math.max(configured, 100)
      : 100;
  };
  
  const getDistanceToStop = (stop) => {
    if (!stop || !currentLocation) return null;

    const gps = getStopGps(stop);

    const currentLat = Number(currentLocation?.lat ?? currentLocation?.latitude);
    const currentLng = Number(currentLocation?.lng ?? currentLocation?.longitude);
    const stopLat = Number(gps?.lat ?? gps?.latitude);
    const stopLng = Number(gps?.lng ?? gps?.longitude);

    if (
      !Number.isFinite(currentLat) ||
      !Number.isFinite(currentLng) ||
      !Number.isFinite(stopLat) ||
      !Number.isFinite(stopLng)
    ) {
      return null;
    }

    return haversineDistance(currentLat, currentLng, stopLat, stopLng);
  };

  const isWithinGeofence = (stop) => {
    if (!stop || !currentLocation) return false;

    const dist = getDistanceToStop(stop);
    if (dist === null) return false;
    return dist <= getStopGeofence(stop);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!run) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <p className="text-zinc-400">Patrol run not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/my-routes')}>
          Back to My Routes
        </Button>
      </div>
    );
  }

  const stops = run.stops || [];
  const summary = run.summary || {};
  // P0: Progress based on completed stops only (pending = outstanding)
  const progress = summary.total_stops > 0 ? (summary.completed / summary.total_stops) * 100 : 0;

  // --- RENDER ---
  return (
    <div className="space-y-4 pb-24" data-testid="patrol-run-page">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/my-routes')}
          className="text-zinc-400"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-rajdhani font-bold text-white flex items-center gap-2">
            <Route className="w-5 h-5 text-blue-400" />
            {run.route_name}
          </h1>
          <p className="text-sm text-zinc-500">{run.site_name}</p>
        </div>
        <Badge className={`${
          run.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
          run.status === 'assigned' ? 'bg-amber-500/20 text-amber-400' :
          run.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
          run.status === 'paused' ? 'bg-yellow-500/20 text-yellow-400' :
          ['auto_closed', 'force_stopped', 'abandoned', 'cancelled', 'expired', 'closed', 'force_closed'].includes(run.status) ? 'bg-red-500/20 text-red-400' :
          'bg-zinc-500/20 text-zinc-400'
        }`}>
          {run.status?.toUpperCase()}
        </Badge>
        {/* P0 FIX: Live tracking status indicator */}
        {run.status === 'in_progress' && (
          <Badge 
            className={`flex items-center gap-1 ${
              (hasLiveGps || isTracking) ? 'bg-emerald-500/20 text-emerald-400' : 
              trackingError ? 'bg-red-500/20 text-red-400' : 
              'bg-amber-500/20 text-amber-400'
            }`}
            title={(hasLiveGps || isTracking) ? `Last ping: ${lastPing?.timestamp || 'N/A'}` : trackingError || 'Tracking not active'}
          >
            <Radio className={`w-3 h-3 ${(hasLiveGps || isTracking) ? 'animate-pulse' : ''}`} />
            {(hasLiveGps || isTracking) ? 'LIVE' : trackingError ? 'ERROR' : 'OFFLINE'}
          </Badge>
        )}
      </div>

      {/* Run not started */}
      {run.status === 'assigned' && (
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-6 text-center">
            <Play className="w-12 h-12 mx-auto text-emerald-400 mb-4" />
            <h2 className="text-lg text-white mb-2">Ready to Start Patrol</h2>
            <p className="text-zinc-500 text-sm mb-4">
              {stops.length} checkpoint{stops.length !== 1 ? 's' : ''} to visit
            </p>
            <Button
              className="bg-emerald-500 hover:bg-emerald-600"
              onClick={handleStartRun}
              disabled={false}
              data-testid="start-run-btn"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
              Start Patrol
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Active Run */}
      {run.status === 'in_progress' && (
        <>
          {/* Progress Card */}
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 text-zinc-400 text-sm">
                    <Timer className="w-4 h-4" />
                    <span className="font-mono">{elapsedTime}</span>
                  </div>
                  <div className={`flex items-center gap-1 text-sm ${
                    gpsStatus === 'locked' ? 'text-emerald-400' : 
                    gpsStatus === 'acquiring' ? 'text-amber-400' : 'text-red-400'
                  }`}>
                    <Target className="w-4 h-4" />
                    <span>{gpsStatus === 'locked' ? `GPS ±${Math.round(currentLocation?.accuracy || 25)}m` : 
                           gpsStatus === 'acquiring' ? 'Acquiring...' : 'No GPS'}</span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                  onClick={() => setShowPauseDialog(true)}
                >
                  <Pause className="w-4 h-4 mr-1" />
                  Pause
                </Button>
                
                {/* PHASE 2: Interrupt button for mobile patrol */}
                {activeShiftId && patrolStatus?.shift_type === 'mobile_patrol' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    onClick={() => setShowInterruptDialog(true)}
                    data-testid="interrupt-patrol-btn"
                  >
                    <StopCircle className="w-4 h-4 mr-1" />
                    Interrupt
                  </Button>
                )}
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Progress</span>
                  <span className="text-zinc-300">{summary.completed}/{summary.total_stops} stops</span>
                </div>
                <Progress value={progress} className="h-2" />
                <div className="flex justify-between text-xs text-zinc-500">
                  <span>{summary.checked_in > 0 ? `${summary.checked_in} in progress` : ''}</span>
                  <span>{summary.pending} remaining</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs: Checklist / Details */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 bg-[#18181b]">
              <TabsTrigger value="checklist" data-testid="tab-checklist">Checklist</TabsTrigger>
              <TabsTrigger value="details" data-testid="tab-details">Stop Details</TabsTrigger>
            </TabsList>

            {/* Checklist Tab */}
            <TabsContent value="checklist" className="mt-4 space-y-2">
              {stops.map((stop, idx) => {
                const status = getStopStatus(stop);
                const dist = getDistanceToStop(stop);
                const withinGeofence = isWithinGeofence(stop);
                const isSelected = selectedStop?.id === stop.id;
                
                return (
                  <Card
                    key={stop.id}
                    className={`bg-[#18181b] cursor-pointer transition-all ${
                      isSelected ? 'border-blue-500' : 'border-[#3f3f46]'
                    } ${status === 'completed' ? 'opacity-60' : ''}`}
                    onClick={() => setSelectedStop(stop)}
                    data-testid={`stop-card-${idx}`}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                          status === 'in_progress' || status === 'checked_in' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-zinc-700 text-zinc-400'
                        }`}>
                          {status === 'completed' ? <Check className="w-4 h-4" /> :
                           status === 'in_progress' || status === 'checked_in' ? <Loader2 className="w-4 h-4 animate-spin" /> :
                           idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{getStopName(stop)}</p>
                          <p className="text-xs text-zinc-500 truncate">{getStopSublabel(stop)}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {dist !== null && (
                            <span className={`text-xs ${withinGeofence ? 'text-emerald-400' : 'text-zinc-400'}`}>
                              {formatDistance(dist)}
                            </span>
                          )}
                          <div className="flex gap-1">
                            {stop.notes?.length > 0 && <MessageSquare className="w-3 h-3 text-blue-400" />}
                            {stop.photos?.length > 0 && <ImageIcon className="w-3 h-3 text-purple-400" />}
                            {stop.incident_ids?.length > 0 && <AlertCircle className="w-3 h-3 text-red-400" />}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </TabsContent>

            {/* Stop Details Tab */}
            <TabsContent value="details" className="mt-4">
              {selectedStop ? (
                <Card className="bg-[#18181b] border-[#3f3f46]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-400" />
                      {getStopName(selectedStop)}
                    </CardTitle>
                    <p className="text-xs text-zinc-500">{getStopSublabel(selectedStop)}</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Status & Distance */}
                    <div className="flex items-center justify-between">
                      <Badge className={`${
                        getStopStatus(selectedStop) === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                        getStopStatus(selectedStop) === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                        getStopStatus(selectedStop) === 'skipped' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {getStopStatus(selectedStop).replace('_', ' ').toUpperCase()}
                      </Badge>
                      {getDistanceToStop(selectedStop) !== null && (
                        <span className={`text-sm ${isWithinGeofence(selectedStop) ? 'text-emerald-400' : 'text-zinc-400'}`}>
                          <Navigation className="w-3 h-3 inline mr-1" />
                          {formatDistance(getDistanceToStop(selectedStop))}
                        </span>
                      )}
                    </div>

                    {/* Check-in/out times */}
                    {selectedStop?.check_in_at && (
                      <div className="text-xs text-zinc-500 space-y-1">
                        <p>Checked in: {formatTime24h(selectedStop?.check_in_at)}</p>
                        {selectedStop?.check_out_at && <p>Checked out: {formatTime24h(selectedStop?.check_out_at)}</p>}
                        {selectedStop?.dwell_seconds && <p>Dwell time: {Math.round(selectedStop?.dwell_seconds / 60)} min</p>}
                      </div>
                    )}

                    
<div className="text-[10px] text-cyan-400 break-all mb-2">
GPSDBG |
gpsStatus={gpsStatus} |
hasLiveGps={String(hasLiveGps)} |
liveLat={String(liveLat)} |
liveLng={String(liveLng)} |
currentLat={String(currentLocation?.lat)} |
currentLng={String(currentLocation?.lng)} |
pings={String(pingCount)}
</div>

{/* Actions */}

                    {getStopStatus(selectedStop) === 'pending' && (
                      <div className="flex gap-2">
                        <Button
                          className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                          onClick={() => handleCheckIn(false)}
                          disabled={false}
                          data-testid="checkin-btn"
                        >
                          {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4 mr-1" />}
                          Check In
                        </Button>
                        <Button
                          variant="outline"
                          className="border-amber-500/50 text-amber-400"
                          onClick={() => setShowSkipDialog(true)}
                          data-testid="skip-btn"
                        >
                          <SkipForward className="w-4 h-4" />
                        </Button>
                      </div>
                    )}

                    {getStopStatus(selectedStop) === 'in_progress' && (
                      <div className="space-y-2">
                        <Button
                          className="w-full bg-blue-500 hover:bg-blue-600"
                          onClick={() => setShowCheckOutDialog(true)}
                          disabled={false}
                          data-testid="checkout-btn"
                        >
                          {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4 mr-1" />}
                          Check Out
                        </Button>
                        <div className="grid grid-cols-3 gap-2">
                          <Button
                            variant="outline"
                            className="border-zinc-600"
                            onClick={() => setShowNoteDialog(true)}
                            data-testid="add-note-btn"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            className="border-zinc-600"
                            onClick={() => setShowPhotoDialog(true)}
                            data-testid="add-photo-btn"
                          >
                            <Camera className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            className="border-zinc-600"
                            onClick={() => setShowLinkIncidentDialog(true)}
                            data-testid="link-incident-btn"
                          >
                            <LinkIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {selectedStop.notes?.length > 0 && (
                      <div>
                        <Label className="text-zinc-400 text-xs">Notes ({selectedStop.notes.length})</Label>
                        <ScrollArea className="h-24 mt-1 bg-[#09090b] rounded p-2">
                          {selectedStop.notes.map((note, i) => (
                            <div key={note.id || i} className="text-xs text-zinc-300 mb-2 pb-2 border-b border-zinc-800 last:border-0">
                              <p>{note.text}</p>
                              <p className="text-zinc-500 mt-1">{formatTime24h(note.created_at)} - {note.created_by_name}</p>
                            </div>
                          ))}
                        </ScrollArea>
                      </div>
                    )}

                    {/* Photos */}
                    {selectedStop.photos?.length > 0 && (
                      <div>
                        <Label className="text-zinc-400 text-xs">Photos ({selectedStop.photos.length})</Label>
                        <div className="flex gap-2 mt-1 overflow-x-auto pb-2">
                          {selectedStop.photos.map((photo, i) => (
                            <div key={photo.id || i} className="flex-shrink-0 w-16 h-16 rounded overflow-hidden bg-zinc-800">
                              <img src={photo.url} alt={photo.caption || 'Photo'} className="w-full h-full object-cover" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Linked Incidents */}
                    {selectedStop.incident_ids?.length > 0 && (
                      <div>
                        <Label className="text-zinc-400 text-xs">Linked Incidents ({selectedStop.incident_ids.length})</Label>
                        <div className="mt-1 space-y-1">
                          {selectedStop.incident_ids.map((incId, i) => (
                            <Badge key={incId} variant="outline" className="text-xs mr-1">
                              IR-{incId.slice(-6)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-[#18181b] border-[#3f3f46]">
                  <CardContent className="p-6 text-center text-zinc-500">
                    Select a stop from the checklist
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>

          {/* Complete Run Button */}
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#09090b] via-[#09090b] to-transparent lg:left-64">
            <div className="max-w-2xl mx-auto">
              <Button
                className="w-full bg-emerald-500 hover:bg-emerald-600 h-12"
                onClick={() => setShowCompleteDialog(true)}
                data-testid="complete-run-btn"
              >
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Complete Patrol Cycle ({summary.completed}/{summary.total_stops})
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Paused State */}
      {run.status === 'paused' && (
        <Card className="bg-[#18181b] border-yellow-500/50">
          <CardContent className="p-6 text-center">
            <Pause className="w-12 h-12 mx-auto text-yellow-400 mb-4" />
            <h2 className="text-lg text-white mb-2">Patrol Paused</h2>
            <p className="text-zinc-500 text-sm mb-4">
              {summary.completed}/{summary.total_stops} stops completed
            </p>
            <Button
              className="bg-blue-500 hover:bg-blue-600"
              onClick={handleResume}
              disabled={false}
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
              Resume Patrol
            </Button>
          </CardContent>
        </Card>
      )}
            {/* Terminal state with explicit recovery CTA — UX fix for auto_closed dead-end */}
      {['auto_closed', 'force_stopped', 'abandoned', 'cancelled', 'expired', 'closed', 'force_closed'].includes(run.status) && (
        <Card className="bg-[#18181b] border-red-500/50" data-testid="patrol-terminal-state">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-red-400 mb-4" />
            <h2 className="text-lg text-white mb-2">Patrol {run.status === 'auto_closed' ? 'Auto-Closed' : 'Ended'}</h2>
            <p className="text-zinc-400 text-sm mb-2">
              {run.status === 'auto_closed'
                ? 'This patrol was auto-closed because the shift ended, GPS became stale, or the patrol exceeded the allowed duration.'
                : `This patrol is no longer active. Status: ${run.status}.`}
            </p>
            {run.auto_closed_reason && (
              <p className="text-zinc-500 text-xs mb-4" data-testid="auto-closed-reason">
                Reason: {String(run.auto_closed_reason).replace(/_/g, ' ')}
              </p>
            )}
            <p className="text-zinc-500 text-sm mb-6">Start a new shift to resume patrols.</p>
            <div className="flex flex-col gap-2">
              <Button
                className="bg-blue-500 hover:bg-blue-600"
                onClick={() => navigate('/my-shifts', { replace: true })}
                data-testid="go-to-shifts-btn"
              >
                Go to My Shifts
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/my-routes', { replace: true })}
                data-testid="back-to-routes-btn"
              >
                Back to My Routes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      {/* Completed State */}
      {run.status === 'completed' && (
        <Card className="bg-[#18181b] border-emerald-500/50">
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-4" />
              <h2 className="text-lg text-white mb-2">Patrol Completed</h2>
              {run.end_time && (
                <p className="text-zinc-500 text-sm">Completed at {formatTime24h(run.end_time)}</p>
              )}
            </div>
            
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-3 bg-[#09090b] rounded">
                <p className="text-2xl font-mono text-emerald-400">{summary.completed}</p>
                <p className="text-xs text-zinc-500">Completed</p>
              </div>
              <div className="text-center p-3 bg-[#09090b] rounded">
                <p className="text-2xl font-mono text-amber-400">{summary.skipped}</p>
                <p className="text-xs text-zinc-500">Skipped</p>
              </div>
              <div className="text-center p-3 bg-[#09090b] rounded">
                <p className="text-2xl font-mono text-blue-400">{summary.total_notes}</p>
                <p className="text-xs text-zinc-500">Notes</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center p-3 bg-[#09090b] rounded">
                <p className="text-xl font-mono text-purple-400">{summary.total_photos}</p>
                <p className="text-xs text-zinc-500">Photos</p>
              </div>
              <div className="text-center p-3 bg-[#09090b] rounded">
                <p className="text-xl font-mono text-red-400">{summary.total_incidents}</p>
                <p className="text-xs text-zinc-500">Incidents</p>
              </div>
            </div>

            {run.notes && (
              <div className="mt-4 p-3 bg-[#09090b] rounded">
                <Label className="text-zinc-400 text-xs">Completion Notes</Label>
                <p className="text-sm text-zinc-300 mt-1">{run.notes}</p>
              </div>
            )}

            <Button
              variant="outline"
              className="w-full mt-4 border-zinc-600"
              onClick={() => navigate('/my-routes')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to My Routes
            </Button>
          </CardContent>
        </Card>
      )}

      {/* --- DIALOGS --- */}

      {/* Check-in Override Dialog */}
      <Dialog open={showCheckInDialog} onOpenChange={setShowCheckInDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <AlertTriangle className="w-5 h-5" />
              Outside Geofence
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              You are {formatDistance(getDistanceToStop(selectedStop))} from {selectedStop?.sector_name} 
              (max {getStopGeofence(selectedStop)}m). Provide a reason to override.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Override Reason</Label>
              <Input
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="e.g., GPS drift, area boundary unclear"
                className="bg-[#09090b] border-[#3f3f46] mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckInDialog(false)} className="border-zinc-600">Cancel</Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600"
              onClick={() => handleCheckIn(true)}
              disabled={!overrideReason.trim() || isSubmitting}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Override & Check In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RAPID_X_V54_CHECKOUT_SECURITY_EVIDENCE */}
      <Dialog
        open={showCheckOutDialog}
        onOpenChange={(open) => {
          if (isSubmitting) return;

          setShowCheckOutDialog(open);

          if (!open) {
            setSecurityStatus('');
            setOfficerObservation('');
          }
        }}
      >
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-blue-400" />
              Complete Checkpoint
            </DialogTitle>

            <DialogDescription className="text-zinc-400">
              Record the security status and your observation for{' '}
              {selectedStop?.sector_name ||
                getStopName(selectedStop)}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>
                Security Status{' '}
                <span className="text-red-400">*</span>
              </Label>

              <Select
                value={securityStatus}
                onValueChange={setSecurityStatus}
                disabled={isSubmitting}
              >
                <SelectTrigger
                  className="bg-[#09090b] border-[#3f3f46] mt-2"
                  data-testid="security-status-select"
                >
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>

                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  <SelectItem value="Secure">
                    Secure
                  </SelectItem>

                  <SelectItem value="Issue Found">
                    Issue Found
                  </SelectItem>

                  <SelectItem value="Unsecure">
                    Unsecure
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>
                Officer Observation{' '}
                <span className="text-red-400">*</span>
              </Label>

              <Textarea
                value={officerObservation}
                onChange={(event) =>
                  setOfficerObservation(event.target.value)
                }
                placeholder="Describe what you inspected and observed..."
                className="bg-[#09090b] border-[#3f3f46] mt-2 min-h-[110px]"
                rows={5}
                disabled={isSubmitting}
                data-testid="officer-observation-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="border-zinc-600"
              disabled={isSubmitting}
              onClick={() => {
                setShowCheckOutDialog(false);
                setSecurityStatus('');
                setOfficerObservation('');
              }}
            >
              Cancel
            </Button>

            <Button
              className="bg-blue-500 hover:bg-blue-600"
              onClick={handleCheckOut}
              disabled={
                isSubmitting ||
                !securityStatus ||
                !officerObservation.trim()
              }
              data-testid="confirm-checkout-btn"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Flag className="w-4 h-4 mr-2" />
              )}

              Confirm Check Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Skip Dialog */}
      {/* Skip Dialog - P0: Reason is now optional */}
      <Dialog open={showSkipDialog} onOpenChange={setShowSkipDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SkipForward className="w-5 h-5 text-amber-400" />
              Skip Stop
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Skipping {selectedStop?.sector_name || getStopName(selectedStop)}. Stop will remain pending for review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Skip Reason (optional)</Label>
              <Textarea
                value={skipReason}
                onChange={(e) => setSkipReason(e.target.value)}
                placeholder="e.g., Area inaccessible, safety concern... (optional)"
                className="bg-[#09090b] border-[#3f3f46] mt-2"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSkipDialog(false)} className="border-zinc-600">Cancel</Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600"
              onClick={handleSkip}
              disabled={false}
              data-testid="confirm-skip-btn"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Skip Stop
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              Add Note
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Note</Label>
              <Textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Enter your observation..."
                className="bg-[#09090b] border-[#3f3f46] mt-2"
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoteDialog(false)} className="border-zinc-600">Cancel</Button>
            <Button
              className="bg-blue-500 hover:bg-blue-600"
              onClick={handleAddNote}
              disabled={!noteText.trim() || isSubmitting}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Dialog - P0.5 Mobile-First with Error Handling */}
      <Dialog open={showPhotoDialog} onOpenChange={(open) => {
        setShowPhotoDialog(open);
        if (!open) {
          setPhotoFile(null);
          setPhotoPreview('');
          setPhotoUrl('');
          setPhotoCaption('');
          setPhotoError('');
        }
      }}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-purple-400" />
              Add Photo Evidence
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Capture or upload photo evidence for this checkpoint
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Error Display */}
            {photoError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <p className="text-red-400 text-sm">{photoError}</p>
              </div>
            )}
            
            {/* Mobile Camera Capture - Primary */}
            <div>
              <Label className="text-zinc-300 font-medium">Take Photo</Label>
              <p className="text-xs text-zinc-500 mb-2">Opens camera on mobile devices</p>
              <div className="mt-2">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoSelect}
                  className="hidden"
                  id="photo-camera-input"
                  data-testid="photo-camera-input"
                />
                <label
                  htmlFor="photo-camera-input"
                  className="flex items-center justify-center gap-3 p-4 border-2 border-dashed border-purple-500/50 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-500/5 transition-all active:scale-95"
                >
                  <Camera className="w-6 h-6 text-purple-400" />
                  <span className="text-purple-300 font-medium">Tap to Open Camera</span>
                </label>
              </div>
            </div>
            
            {/* Gallery/File Upload - Secondary */}
            <div>
              <Label className="text-zinc-300 font-medium">Or Upload from Gallery</Label>
              <div className="mt-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="hidden"
                  id="photo-gallery-input"
                  data-testid="photo-gallery-input"
                />
                <label
                  htmlFor="photo-gallery-input"
                  className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-zinc-600 rounded-lg cursor-pointer hover:border-zinc-500 transition-colors"
                >
                  <Upload className="w-5 h-5 text-zinc-400" />
                  <span className="text-zinc-400">Choose from gallery</span>
                </label>
              </div>
            </div>
            
            {/* Photo Preview */}
            {photoPreview && (
              <div className="relative">
                <Label className="text-zinc-400 text-xs">Preview</Label>
                <div className="mt-1 relative rounded-lg overflow-hidden border border-emerald-500/30">
                  <img 
                    src={photoPreview} 
                    alt="Preview" 
                    className="w-full h-40 object-cover" 
                  />
                  <div className="absolute top-2 right-2 flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 bg-black/70 hover:bg-black/90"
                      onClick={() => { 
                        setPhotoFile(null); 
                        setPhotoPreview(''); 
                        setPhotoError('');
                      }}
                    >
                      <X className="w-4 h-4 text-white" />
                    </Button>
                  </div>
                  <div className="absolute bottom-2 left-2">
                    <Badge className="bg-emerald-500/80 text-white text-xs">
                      Ready to upload
                    </Badge>
                  </div>
                </div>
              </div>
            )}

            <div className="text-center text-xs text-zinc-500 py-1">— or enter URL —</div>

            {/* URL Input - Fallback */}
            <div>
              <Label className="text-zinc-400 text-xs">Photo URL (optional)</Label>
              <Input
                value={photoUrl}
                onChange={(e) => setPhotoUrl(e.target.value)}
                placeholder="https://..."
                className="bg-[#09090b] border-[#3f3f46] mt-1"
                disabled={!!photoFile}
              />
            </div>

            {/* Caption */}
            <div>
              <Label className="text-zinc-400 text-xs">Caption (optional)</Label>
              <Input
                value={photoCaption}
                onChange={(e) => setPhotoCaption(e.target.value)}
                placeholder="Describe what the photo shows..."
                className="bg-[#09090b] border-[#3f3f46] mt-1"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowPhotoDialog(false)} 
              className="border-zinc-600"
            >
              Cancel
            </Button>
            <Button
              className="bg-purple-500 hover:bg-purple-600"
              onClick={handleAddPhoto}
              disabled={(!photoFile && !photoUrl.trim()) || uploadingPhoto}
              data-testid="upload-photo-btn"
            >
              {uploadingPhoto ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Add Photo
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Incident Dialog - P0: Now includes Create New Incident */}
      <Dialog open={showLinkIncidentDialog} onOpenChange={(open) => {
        setShowLinkIncidentDialog(open);
        if (!open) {
          setShowCreateIncident(false);
          setNewIncidentTitle('');
          setNewIncidentDescription('');
          setNewIncidentPhoto(null);
          setNewIncidentPhotoPreview('');
        }
      }}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-red-400" />
              {showCreateIncident ? 'Create New Incident' : 'Link Incident Report'}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {showCreateIncident 
                ? 'Create and link a new incident to this checkpoint'
                : 'Link an existing incident or create a new one'
              }
            </DialogDescription>
          </DialogHeader>
          
          {!showCreateIncident ? (
            // Link existing incident view
            <div className="space-y-4">
              <div>
                <Label>Select Existing Incident</Label>
                <Select value={selectedIncidentId} onValueChange={setSelectedIncidentId}>
                  <SelectTrigger className="bg-[#09090b] border-[#3f3f46] mt-2">
                    <SelectValue placeholder="Choose an incident..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                    {availableIncidents.length === 0 ? (
                      <div className="p-2 text-sm text-zinc-500 text-center">No incidents available</div>
                    ) : (
                      availableIncidents.map(inc => (
                        <SelectItem key={inc.id} value={inc.id}>
                          {inc.ir_number || inc.id.slice(-6)} - {inc.title}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="text-center text-xs text-zinc-500 my-2">— or —</div>
              
              {/* P0: Create New Incident Button */}
              <Button
                variant="outline"
                className="w-full border-amber-500 text-amber-400 hover:bg-amber-500/10"
                onClick={() => setShowCreateIncident(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New Incident
              </Button>
            </div>
          ) : (
            // Create new incident form
            <div className="space-y-4">
              <div>
                <Label>Incident Type</Label>
                <Select value={newIncidentType} onValueChange={setNewIncidentType}>
                  <SelectTrigger className="bg-[#09090b] border-[#3f3f46] mt-2">
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                    <SelectItem value="security">Security</SelectItem>
                    <SelectItem value="theft">Theft</SelectItem>
                    <SelectItem value="damage">Damage</SelectItem>
                    <SelectItem value="suspicious_activity">Suspicious Activity</SelectItem>
                    <SelectItem value="trespass">Trespass</SelectItem>
                    <SelectItem value="alarm">Alarm</SelectItem>
                    <SelectItem value="medical">Medical</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Title *</Label>
                <Input
                  value={newIncidentTitle}
                  onChange={(e) => setNewIncidentTitle(e.target.value)}
                  placeholder="Brief description..."
                  className="bg-[#09090b] border-[#3f3f46] mt-2"
                />
              </div>
              
              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  value={newIncidentDescription}
                  onChange={(e) => setNewIncidentDescription(e.target.value)}
                  placeholder="Detailed description..."
                  className="bg-[#09090b] border-[#3f3f46] mt-2 min-h-[80px]"
                />
              </div>
              
              {/* Mobile Camera/Photo Capture - P0.5 Enhanced */}
              <div>
                <Label className="text-zinc-300 font-medium">Photo Evidence (optional)</Label>
                <p className="text-xs text-zinc-500 mt-1 mb-2">Capture photo evidence for this incident</p>
                
                {/* Error Display */}
                {photoError && (
                  <div className="p-2 mb-2 bg-red-500/10 border border-red-500/30 rounded flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                    <p className="text-red-400 text-xs">{photoError}</p>
                  </div>
                )}
                
                <div className="space-y-2">
                  {/* Camera Capture - Primary */}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleIncidentPhotoSelect}
                    className="hidden"
                    id="incident-photo-camera"
                    data-testid="incident-photo-camera"
                  />
                  <label
                    htmlFor="incident-photo-camera"
                    className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-amber-500/50 rounded-lg cursor-pointer hover:border-amber-400 hover:bg-amber-500/5 transition-all active:scale-95"
                  >
                    <Camera className="w-5 h-5 text-amber-400" />
                    <span className="text-amber-300 text-sm font-medium">Take Photo</span>
                  </label>
                  
                  {/* Gallery - Secondary */}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleIncidentPhotoSelect}
                    className="hidden"
                    id="incident-photo-gallery"
                    data-testid="incident-photo-gallery"
                  />
                  <label
                    htmlFor="incident-photo-gallery"
                    className="flex items-center justify-center gap-2 p-2 border border-zinc-600 rounded-lg cursor-pointer hover:border-zinc-500 transition-colors text-sm"
                  >
                    <Upload className="w-4 h-4 text-zinc-400" />
                    <span className="text-zinc-400">Choose from gallery</span>
                  </label>
                </div>
                
                {/* Photo Preview */}
                {newIncidentPhotoPreview && (
                  <div className="mt-3 relative rounded-lg overflow-hidden border border-emerald-500/30">
                    <img 
                      src={newIncidentPhotoPreview} 
                      alt="Preview" 
                      className="w-full h-28 object-cover" 
                    />
                    <div className="absolute top-2 right-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 bg-black/70 hover:bg-black/90"
                        onClick={() => { 
                          setNewIncidentPhoto(null); 
                          setNewIncidentPhotoPreview(''); 
                          setPhotoError('');
                        }}
                      >
                        <X className="w-4 h-4 text-white" />
                      </Button>
                    </div>
                    <div className="absolute bottom-2 left-2">
                      <Badge className="bg-emerald-500/80 text-white text-xs">
                        Photo ready
                      </Badge>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            {showCreateIncident ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setShowCreateIncident(false)} 
                  className="border-zinc-600"
                >
                  Back
                </Button>
                <Button
                  className="bg-red-500 hover:bg-red-600"
                  onClick={handleCreateIncident}
                  disabled={!newIncidentTitle.trim() || creatingIncident}
                >
                  {creatingIncident && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Create & Link
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowLinkIncidentDialog(false)} className="border-zinc-600">
                  Cancel
                </Button>
                <Button
                  className="bg-red-500 hover:bg-red-600"
                  onClick={handleLinkIncident}
                  disabled={!selectedIncidentId || isSubmitting}
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Link Incident
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              Complete Patrol Cycle
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {summary.completed}/{summary.total_stops} stops completed
              {summary.pending > 0 && (
                <span className="text-amber-400 block mt-1">
                  {summary.pending} stop(s) not visited
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Completion Notes (optional)</Label>
              <Textarea
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder="Any final observations..."
                className="bg-[#09090b] border-[#3f3f46] mt-2"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteDialog(false)} className="border-zinc-600">Cancel</Button>
            <Button
              className="bg-emerald-500 hover:bg-emerald-600"
              onClick={handleCompleteRun}
              disabled={false}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Complete Patrol Cycle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pause Dialog */}
      <Dialog open={showPauseDialog} onOpenChange={setShowPauseDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pause className="w-5 h-5 text-yellow-400" />
              Pause Patrol
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Pause Notes (optional)</Label>
              <Textarea
                value={pauseNotes}
                onChange={(e) => setPauseNotes(e.target.value)}
                placeholder="Reason for pausing..."
                className="bg-[#09090b] border-[#3f3f46] mt-2"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPauseDialog(false)} className="border-zinc-600">Cancel</Button>
            <Button
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              onClick={handlePause}
              disabled={false}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Pause Patrol
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* PHASE 2: Patrol Interruption Modal */}
      <PatrolInterruptionModal
        open={showInterruptDialog}
        onOpenChange={setShowInterruptDialog}
        shiftId={activeShiftId}
        onInterrupted={(result) => {
          toast.success('Patrol interrupted - you may now sign off');
          navigate('/my-shifts');
        }}
      />
    </div>
  );
}
